<template>
  <div class="content">
    <div class="container-fluid">
      <div class="row">
        <div class="col-md-12">
          <div class="card">
            <div class="card-header">
               <div class="row align-items-center">
                 <div class="col-md-6">
                    <h4 class="card-title">Purchase Queue Management</h4>
                 </div>
                 <div class="col-md-6">
                    <div class="d-flex align-items-center justify-content-end">
                      <div class="control-group mr-3 mb-2">
                        <label class="control-label text-muted small text-uppercase font-weight-bold mr-2 mb-0">Refresh</label>
                        <div class="select-wrapper">
                            <select v-model="refreshInterval" @change="handleRefreshChange" class="custom-select-modern">
                                <option v-for="opt in refreshOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                            </select>
                            <i class="fas fa-chevron-down select-icon"></i>
                        </div>
                      </div>

                      <button @click="fetchData" class="btn btn-primary btn-round btn-icon shadow-sm mb-2" :disabled="loading" style="width: 42px; height: 42px; border-radius: 50%; padding: 0; display: flex; align-items: center; justify-content: center;">
                        <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
                      </button>
                    </div>
                 </div>
               </div>
            </div>
            <div class="card-body">

    <!-- Filters Section -->
    <div class="filters-section">
      <div class="filter-row">
        <div class="filter-group">
          <label>Filter Status:</label>
          <select v-model="filterStatus" class="form-control">
            <option v-for="opt in filterOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Table Section -->
    <div class="table-section bg-transparent shadow-none">
       <!-- Loading State -->
       <div v-if="loading && filteredItems.length === 0" class="text-center py-5">
           <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
           </div>
           <div class="mt-2 text-muted">Loading purchase queue...</div>
       </div>
       
       <!-- Empty State -->
       <div v-else-if="filteredItems.length === 0" class="text-center py-5 card">
            <div class="card-body text-muted">
                <i class="fas fa-inbox fa-3x mb-3 opacity-50"></i>
                <p>No records found matching your criteria</p>
            </div>
       </div>

       <!-- Card List -->
       <div v-else class="transaction-list">
          <div v-for="item in filteredItems" :key="item.id" class="card border-0 mb-3 shadow-sm transaction-card">
              <div class="card-body p-3">
                  <div class="row align-items-center">
                      <!-- Left: Status, Primary Logic & Date -->
                      <div class="col-lg-4 col-md-5 col-12 mb-3 mb-md-0">
                          <div class="d-flex align-items-center mb-2">
                              <span class="badge mr-2 px-2 py-1" :class="getBadgeClass(item)">
                                  <i class="fas mr-1" :class="getBadgeIcon(item)"></i>
                                  {{ item.parking_pass_url ? 'Completed' : 'Pending' }}
                              </span>
                              <span class="badge badge-soft-secondary text-muted border">
                                  ID: {{ item.id }}
                              </span>
                          </div>
                          
                          <h6 class="font-weight-bold text-dark mb-1 text-truncate" :title="item.primary_name">
                              {{ item.primary_name || 'Unknown Primary' }}
                          </h6>
                          
                          <div class="text-muted small mt-1" v-if="item.external_invoice_ref">
                             <span class="text-xs text-uppercase font-weight-bold opacity-7" style="font-size: 0.7rem;">Ref:</span> 
                             <span class="text-monospace">{{ item.external_invoice_ref }}</span>
                          </div>
                          
                          <div class="text-muted very-small mt-1">
                              <i class="far fa-clock mr-1"></i> 
                              {{ new Date(item.created_at).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) }}
                          </div>
                      </div>

                      <!-- Middle: Location & Qty -->
                      <div class="col-lg-4 col-md-4 col-12 mb-3 mb-md-0 border-left-lg pl-lg-4">
                           <div class="row no-gutters align-items-center">
                                <div class="col-auto mr-3">
                                      <div class="text-center bg-soft-primary rounded p-2" style="min-width: 60px;">
                                          <div class="h4 font-weight-bold text-primary mb-0" style="line-height: 1;">{{ item.quantity }}</div>
                                          <div class="very-small text-uppercase font-weight-bold text-primary opacity-7">Qty</div>
                                      </div>
                                </div>
                                <div class="col">
                                    <div class="h6 font-weight-bold mb-0 text-dark text-truncate" :title="item.primary_location_name">{{ item.primary_location_name || 'N/A' }}</div>
                                    <div class="small text-muted mb-1">Primary Location</div>
                                    
                                    <a v-if="item.primary_location_url" :href="item.primary_location_url" target="_blank" class="badge badge-info text-white p-1 px-2" style="text-decoration: none;">
                                        <i class="fas fa-external-link-alt mr-1"></i> View Loc
                                    </a>
                                </div>
                           </div>
                      </div>

                      <!-- Right: Actions -->
                      <div class="col-lg-4 col-md-3 col-12 d-flex flex-wrap justify-content-end align-items-center border-left-lg pl-lg-3 mt-md-0">
                          
                          <div class="w-100 text-right mb-2">
                              <a v-if="item.parking_pass_url" :href="item.parking_pass_url" target="_blank" class="btn btn-sm btn-success shadow-sm rounded-pill px-3">
                                  <i class="fas fa-ticket-alt mr-1"></i> View Pass
                              </a>
                              <span v-else class="badge badge-light border text-muted px-3 py-2">
                                  <i class="fas fa-ban mr-1"></i> No Pass
                              </span>
                          </div>

                          <div class="w-100 text-right">
                               <button @click="editItem(item)" class="btn btn-light btn-icon btn-round border shadow-sm mr-2" title="Edit">
                                  <i class="fas fa-edit text-primary"></i>
                               </button>
                               <button @click="deleteItem(item)" class="btn btn-light btn-icon btn-round border shadow-sm" title="Delete">
                                  <i class="fas fa-trash text-danger"></i>
                               </button>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
       </div>
    </div>

          </div>
        </div>
      </div>
    </div>
  </div>
    <!-- Add/Edit Modal -->
    <b-modal id="item-modal" :title="modalTitle" @ok="handleModalOk" header-bg-variant="light" header-text-variant="dark">
      <form ref="form" @submit.stop.prevent="handleSubmit" class="venue-form">
        <div class="form-section">
          <h4>Record Details</h4>
          <div class="form-row">
            <div class="form-group">
              <label for="external_invoice_ref">External Invoice Ref:</label>
              <b-form-input
                id="external_invoice_ref"
                v-model="form.external_invoice_ref"
              ></b-form-input>
            </div>
            <div class="form-group">
              <label for="primary_name">Primary Name:</label>
              <b-form-input
                id="primary_name"
                v-model="form.primary_name"
              ></b-form-input>
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label for="primary_location_name">Primary Location Name:</label>
              <b-form-input
                id="primary_location_name"
                v-model="form.primary_location_name"
              ></b-form-input>
            </div>
            <div class="form-group">
              <label for="quantity">Quantity:</label>
              <b-form-input
                id="quantity"
                v-model="form.quantity"
                type="number"
              ></b-form-input>
            </div>
          </div>

          <div class="form-group">
            <label for="primary_location_url">Primary Location URL:</label>
            <b-form-input
              id="primary_location_url"
              v-model="form.primary_location_url"
            ></b-form-input>
          </div>

          <div class="form-group">
            <label for="parking_pass_url">Parking Pass URL:</label>
            <b-form-input
              id="parking_pass_url"
              v-model="form.parking_pass_url"
            ></b-form-input>
          </div>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'PurchaseQueue',
  data() {
    return {
      items: [],
      loading: false,
      filterStatus: 'pending',
      filterOptions: [
        { value: 'all', text: 'Show All' },
        { value: 'pending', text: 'Pending (No Pass URL)' },
        { value: 'completed', text: 'Completed (Has Pass URL)' }
      ],
      refreshInterval: 60000, // Default 1 minute
      refreshOptions: [
        { value: 0, text: 'Off' },
        { value: 10000, text: '10 seconds' },
        { value: 30000, text: '30 seconds' },
        { value: 60000, text: '1 minute' },
        { value: 300000, text: '5 minutes' }
      ],
      timer: null,
      form: {
        id: null,
        external_invoice_ref: '',
        primary_name: '',
        primary_location_name: '',
        primary_location_url: '',
        quantity: null,
        parking_pass_url: ''
      },
      modalTitle: 'Add New Record'
    };
  },
  computed: {
    filteredItems() {
      if (this.filterStatus === 'pending') {
        return this.items.filter(item => !item.parking_pass_url);
      } else if (this.filterStatus === 'completed') {
        return this.items.filter(item => item.parking_pass_url);
      }
      return this.items;
    }
  },
  mounted() {
    this.fetchData();
    this.startTimer();
  },
  beforeDestroy() {
    this.stopTimer();
  },
  methods: {
    getBadgeClass(item) {
        if (!item.parking_pass_url) return 'badge-warning bg-soft-warning text-warning';
        return 'badge-success bg-soft-success text-success';
    },
    getBadgeIcon(item) {
        if (!item.parking_pass_url) return 'fa-clock';
        return 'fa-check';
    },
    async fetchData() {
      this.loading = true;
      try {
        const response = await fetch('https://api.spotjockeys.com/api/purchase-queue');
        if (!response.ok) throw new Error('Failed to fetch data');
        this.items = await response.json();
      } catch (error) {
        console.error(error);
        this.$bvToast.toast('Error fetching data', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    },
    startTimer() {
      this.stopTimer();
      if (this.refreshInterval > 0) {
        this.timer = setInterval(this.fetchData, this.refreshInterval);
      }
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    handleRefreshChange() {
      this.startTimer();
    },
    showAddModal() {
      this.resetForm();
      this.modalTitle = 'Add New Record';
      this.$bvModal.show('item-modal');
    },
    editItem(item) {
      this.form = { ...item };
      this.modalTitle = 'Edit Record';
      this.$bvModal.show('item-modal');
    },
    resetForm() {
      this.form = {
        id: null,
        external_invoice_ref: '',
        primary_name: '',
        primary_location_name: '',
        primary_location_url: '',
        quantity: null,
        parking_pass_url: ''
      };
    },
    handleModalOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleSubmit();
    },
    async handleSubmit() {
      try {
        const url = this.form.id
          ? `https://api.spotjockeys.com/api/purchase-queue/${this.form.id}`
          : 'https://api.spotjockeys.com/api/purchase-queue';
        
        const method = this.form.id ? 'PUT' : 'POST';

        const response = await fetch(url, {
          method: method,
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.form)
        });

        if (!response.ok) throw new Error('Operation failed');

        this.$bvModal.hide('item-modal');
        this.fetchData();
        this.$bvToast.toast('Operation successful', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        console.error(error);
        this.$bvToast.toast('Operation failed', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },
    async deleteItem(item) {
      if (confirm('Are you sure you want to delete this record?')) {
        try {
          const response = await fetch(`https://api.spotjockeys.com/api/purchase-queue/${item.id}`, {
            method: 'DELETE'
          });

          if (!response.ok) throw new Error('Delete failed');

          this.fetchData();
          this.$bvToast.toast('Record deleted', {
            title: 'Success',
            variant: 'success',
            solid: true
          });
        } catch (error) {
          console.error(error);
          this.$bvToast.toast('Delete failed', {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
        }
      }
    }
  }
};
</script>

<style scoped>
/* Card & list styles similar to SalesDashboard */
.transaction-card {
    border-radius: 12px !important;
    transition: all 0.2s ease;
    border: 1px solid #edf2f9 !important;
    background-color: #fff;
}
.transaction-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 .5rem 1rem rgba(0,0,0,.08) !important;
    border-color: #dbe2ef !important;
}

/* Utility Backgrounds */
.bg-soft-primary { background-color: rgba(54, 185, 204, 0.1); }
.text-primary { color: #36b9cc !important; }
.bg-soft-success { background-color: rgba(28, 200, 138, 0.1); }
.bg-soft-warning { background-color: rgba(246, 194, 62, 0.1); }
.bg-soft-danger { background-color: rgba(231, 74, 59, 0.1); }
.bg-soft-secondary { background-color: #f8f9fa; }
.text-light-gray { color: #d1d3e2; }

/* Text Utilities */
.very-small {
    font-size: 0.65rem;
    letter-spacing: 0.5px;
}
.opacity-7 { opacity: 0.7; }

/* Buttons */
.btn-icon {
    width: 32px;
    height: 32px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

@media (min-width: 992px) {
    .border-left-lg {
        border-left: 1px solid #eaecf4;
    }
}

.venue-management {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.header-section h2 {
  margin: 0;
  color: #2c3e50;
  font-size: 24px;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.filters-section {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #dee2e6;
}

.filter-row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-group label {
  margin: 0;
  font-weight: 600;
  color: #495057;
  white-space: nowrap;
}

/* Form Styles */
.venue-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-section {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 15px;
}

.form-section h4 {
  margin: 0 0 15px 0;
  color: #495057;
  font-size: 16px;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 8px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.form-group label {
  margin-bottom: 5px;
  font-weight: 600;
  color: #495057;
}

.fa-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Select wrapper from dashboard */
.select-wrapper {
    position: relative;
    display: inline-block;
}

.custom-select-modern {
    appearance: none;
    -webkit-appearance: none;
    background-color: transparent;
    border: none;
    padding-right: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #495057;
    cursor: pointer;
    line-height: 1.2;
}

.custom-select-modern:focus {
    outline: none;
}

.select-icon {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: #6c757d;
    font-size: 0.8rem;
}

.control-group {
    display: flex;
    align-items: center;
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 20px;
    padding: 6px 15px;
}

.select-wrapper {
    position: relative;
    display: inline-block;
}

.custom-select-modern {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background: transparent;
    border: none;
    padding-right: 20px;
    padding-left: 5px;
    font-size: 16px;
    font-weight: 600;
    color: #2c3e50;
    cursor: pointer;
    line-height: 1.5;
    outline: none;
    min-width: 100px;
}

.select-icon {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: #6c757d;
    font-size: 0.8rem;
}
</style>
